import React from 'react';
import ServiceLayout from '../components/ServiceLayout';
import { CheckCircle2, ArrowRight, Calendar, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const GeneraliseretAngst = () => {
  const canonicalUrl = "https://corehypnose.dk/generaliseret-angst";

  const treatmentSteps = [
    "Forstå dine bekymringer: Jeg starter med at afmystificere generaliseret angst, så du forstår, hvorfor tankerne kører i ring, og hvordan du kan bryde mønstret.",
    "Metakognitive teknikker: Du lærer at give slip på overtænkning og bekymringer, så de ikke længere styrer din dag eller dine nætter.",
    "Hypnose: Hypnosen hjælper dig med at finde ro i kroppen, skabe klarhed i sindet og styrke dine indre ressourcer.",
    "Enkle hjemmeøvelser: Du får praktiske værktøjer, du nemt kan bruge i hverdagen, så forandringen bliver ved med at virke.",
    "Hold fast i det nye: Jeg lægger en plan sammen med dig, så du undgår tilbagefald og bevarer din mentale frihed, også når livet presser på."
  ];

  const symptoms = [
    "Vedvarende bekymringer – også om småting",
    "Tankemylder og svært ved at “slukke hjernen”",
    "Søvnbesvær og træthed",
    "Indre uro og rastløshed",
    "Spændinger i nakke, skuldre eller mave",
    "Koncentrationsbesvær",
    "Bekymringer om helbred, fremtid eller relationer",
    "Oplevelse af at være “på overarbejde” mentalt",
    "Frygt for at miste kontrollen"
  ];

  return (
    <ServiceLayout
      title="Generaliseret angst – Slip bekymringerne og få ro"
      subtitle="Hypnose og metakognitiv terapi hjælper dig med at stoppe overtænkning og få ro i kroppen og hovedet."
      description={`Generaliseret angst føles som en konstant alarmtilstand. Du bekymrer dig, ofte om alt muligt på en gang, og det føles, som om hjernen ikke kan slukke. Tankerne kredser, kroppen er urolig, og det bliver svært at være nærværende.\n\nAngsten viser sig som tankemylder, søvnbesvær, spændinger og en grundlæggende uro. Mange beskriver det som en indre rastløshed og en følelse af ikke længere at have styr på sig selv eller sit liv.`}
      image="https://assets.auxodev.dk/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/adcfbb22-1127-422c-b05e-ace07d1b49f6.png"
      content={treatmentSteps}
      showContentSection={false}
      metaTitle="Generaliseret angst | Slip bekymringerne med hypnose & metakognitiv terapi"
      metaDescription="Effektiv behandling af generaliseret angst med hypnose og metakognitiv terapi. Ro i hovedet og kroppen. +25 års erfaring. Book en intro-session i dag"
      actions={
        <>
          <Link to="/bestil-tid" className="inline-flex items-center gap-2 bg-[#193f10] text-white px-4 py-3 rounded-md font-bold text-sm sm:text-base hover:bg-[#193f10]/90 transition-all shadow-md shrink-0">
            <Calendar size={18} /> Book første behandlingssession
          </Link>
          <a href="tel:27204671" className="inline-flex items-center gap-2 border-2 border-[#193f10] text-[#193f10] px-4 py-3 rounded-md font-bold text-sm sm:text-base hover:bg-[#faf7f2] transition-all shrink-0">
            <Phone size={18} /> Tlf. 27 20 46 71
          </a>
        </>
      }
    >
      <Helmet>
        <title>Generaliseret angst | Slip bekymringerne med hypnose & metakognitiv terapi</title>
        <meta name="description" content="Effektiv behandling af generaliseret angst med hypnose og metakognitiv terapi. Ro i hovedet og kroppen. +25 års erfaring. Book en intro-session i dag" />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content="Generaliseret angst | Slip bekymringerne med hypnose & metakognitiv terapi" />
        <meta property="og:description" content="Effektiv behandling af generaliseret angst med hypnose og metakognitiv terapi. Ro i hovedet og kroppen. +25 års erfaring. Book en intro-session i dag" />
        <meta property="og:image" content="" />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="" />
      </Helmet>

      {/* Kundeerfaring */}
      <div className="mb-20 bg-[#123F13] text-white p-8 md:p-12 rounded-2xl relative overflow-hidden">
        <div className="relative z-10">
          <h2 className="text-2xl md:text-3xl font-serif mb-6">“Det var som om hovedet blev stille for første gang i årevis”</h2>
          <p className="text-lg opacity-90 mb-6 leading-relaxed">
            Jens på 55 år havde i mange år levet med konstant tankemylder, søvnbesvær og en evig følelse af indre uro. Han beskrev det som “at være i gang mentalt hele tiden, uden pause”.
          </p>
          <p className="text-lg opacity-90 mb-8 leading-relaxed">
            Gennem metakognitive teknikker og hypnose lærte Jens at give slip på bekymringerne, og langsomt genvandt han sin ro, sit overskud og sin tro på sig selv. Jens troede, det var for sent at få det bedre, men opdagede, at det aldrig er for sent.
          </p>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[#8C2F12] rounded-full flex items-center justify-center font-bold text-xl">J</div>
            <div>
              <p className="font-bold">Jens, 55 år</p>
              <p className="text-sm opacity-70">Tidligere klient</p>
            </div>
          </div>
        </div>
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32 blur-3xl"></div>
      </div>

      {/* Hvorfor bliver det ved? */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-20 items-center">
        <div>
          <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mb-6">Hvorfor bliver bekymringerne ved?</h2>
          <div className="space-y-4 text-[#3B352F]/70 leading-relaxed text-lg">
            <p>
              Det er sjældent en enkelt tanke, der skaber problemet, det er måden, du forholder dig til tankerne på. Når du prøver at kontrollere dem, analysere dem eller få ro gennem sikkerhedstjek og forberedelse, så føles det måske som en hjælp. Men i længden holder det bekymringerne i gang.
            </p>
            <p>
              Hos Core Hypnose lærer du at lade tankerne være, uden at skulle finde svar på dem. Du opdager, hvordan du kan få mere ro og energi, uden at ændre på alt, men ved at ændre dit forhold til tankerne.
            </p>
          </div>
        </div>
        <div className="bg-[#E8DDCE]/30 p-8 rounded-2xl border border-[#E8DDCE]">
          <h3 className="text-xl font-bold text-[#123F13] mb-6">Typiske symptomer</h3>
          <ul className="space-y-3">
            {symptoms.map((symptom, i) => (
              <li key={i} className="flex items-start gap-3 text-[#3B352F]/80">
                <CheckCircle2 className="w-5 h-5 text-[#8C2F12] mt-1 shrink-0" />
                <span>{symptom}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Hvorfor det virker */}
      <div className="mb-20">
        <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mb-8">Hvorfor min behandling mod generaliseret angst virker</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="bg-white p-8 rounded-2xl border border-[#E8DDCE] shadow-sm">
            <h3 className="text-xl font-bold text-[#123F13] mb-4">Her og nu fokus</h3>
            <p className="text-[#3B352F]/70">
              Jeg fokuserer på det, du mærker og tænker lige nu, i stedet for at grave i gamle traumer. Det giver hurtigere resultater.
            </p>
          </div>
          <div className="bg-white p-8 rounded-2xl border border-[#E8DDCE] shadow-sm">
            <h3 className="text-xl font-bold text-[#123F13] mb-4">Evidensbaseret</h3>
            <p className="text-[#3B352F]/70">
              Du lærer metoder fra metakognitiv terapi, der er videnskabeligt bevist til at reducere bekymringer og angst effektivt.
            </p>
          </div>
          <div className="bg-white p-8 rounded-2xl border border-[#E8DDCE] shadow-sm">
            <h3 className="text-xl font-bold text-[#123F13] mb-4">Kombineret effekt</h3>
            <p className="text-[#3B352F]/70">
              Ved at kombinere terapi med hypnose arbejder jeg både med dine bevidste strategier og dit ubevidste nervesystem.
            </p>
          </div>
        </div>
      </div>

      {/* FAQ & Ressourcer */}
      <div className="mb-20 grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-[#FAF7F2] p-8 rounded-2xl border border-[#E8DDCE]">
          <h3 className="text-xl font-bold text-[#123F13] mb-6">FAQ - svar på dine spørgsmål</h3>
          <ul className="space-y-4">
            <li className="border-b border-[#E8DDCE] pb-2">
              <Link to="/faq" className="flex items-center justify-between group text-[#3B352F] hover:text-[#8C2F12] transition-colors">
                <span>Hvor mange sessioner har jeg brug for?</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </li>
            <li className="border-b border-[#E8DDCE] pb-2">
              <Link to="/faq" className="flex items-center justify-between group text-[#3B352F] hover:text-[#8C2F12] transition-colors">
                <span>Er hypnose sikkert?</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </li>
            <li className="border-b border-[#E8DDCE] pb-2">
              <Link to="/online-sessioner" className="flex items-center justify-between group text-[#3B352F] hover:text-[#8C2F12] transition-colors">
                <span>Kan jeg få behandling online?</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </li>
          </ul>
          <Link to="/faq" className="inline-block mt-6 text-[#8C2F12] font-bold hover:underline">
            Se alle spørgsmål og svar
          </Link>
        </div>
        <div className="bg-[#123F13] p-8 rounded-2xl text-white">
          <h3 className="text-xl font-bold mb-6">Vil du vide mere om angst?</h3>
          <p className="opacity-80 mb-6">
            Oplever du også søvnbesvær eller udmattelse i forbindelse med dine bekymringer? Læs mere om, hvordan jeg kan hjælpe dig med at genvinde din nattesøvn.
          </p>
          <Link 
            to="/soevnproblemer" 
            className="inline-flex items-center gap-2 bg-white text-[#123F13] px-6 py-3 rounded-full font-bold hover:bg-[#E8DDCE] transition-colors"
          >
            Læs om søvnproblemer
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {/* Hvordan foregår behandlingen? - Flyttet hertil */}
      <div className="mb-20">
        <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mb-12">Hvordan foregår behandlingen?</h2>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {treatmentSteps.map((step, index) => {
            const parts = step.split(':');
            const title = parts[0];
            const desc = parts[1];
            
            // Logic for col-span (5 items)
            const colSpan = index < 3 ? "lg:col-span-4" : "lg:col-span-6";

            return (
              <div 
                key={index} 
                className={`bg-white p-8 rounded-2xl border border-[#E8DDCE] shadow-sm hover:shadow-md transition-shadow flex flex-col h-full md:col-span-6 ${colSpan}`}
              >
                <div className="flex items-center gap-4 mb-4">
                  <span className="text-4xl font-serif text-[#8C2F12]/20 font-bold">
                    {(index + 1).toString().padStart(2, '0')}
                  </span>
                  <h3 className="text-xl font-bold text-[#123F13] leading-tight">{title}</h3>
                </div>
                <p className="text-[#3B352F]/70 leading-relaxed">{desc}</p>
              </div>
            );
          })}
        </div>

        <div className="text-center pb-16 mt-20">
          <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mb-8">Klar til at få ro og kontrol igen?</h2>
          <Link to="/bestil-tid" className="inline-flex items-center gap-2 bg-[#8C2F12] text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-[#a33a1a] transition-all shadow-lg">
            Book din første session nu <ArrowRight size={20} />
          </Link>
        </div>
      </div>
    </ServiceLayout>
  );
};

export default GeneraliseretAngst;
